import { useState } from 'react';
import { Flex, SimpleGrid } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getPersonBankAccounts } from '@dealstack/frontend/app/services/Person.service.ts';
import { ManageBankDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ManageBankDrawer/ManageBankDrawer.tsx';
import { ManageLegalDocumentsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ManageLegalDocumentsDrawer/ManageLegalDocumentsDrawer.tsx';
import { useInvestmentDetailsBankInformation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsBankInformation.ts';
import { InvestmentAllocations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocations.tsx';
import { InvestmentLegalDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentLegalDocuments/InvestmentLegalDocuments.tsx';
import { InvestmentBankInformation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentBankInformation/InvestmentBankInformation.tsx';
import { InvestmentOtherData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentOtherData/InvestmentOtherData.tsx';
import { InvestmentTaxCompliance } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentTaxCompliance/InvestmentTaxCompliance.tsx';
import { InvestmentKycStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentKycStatus/InvestmentKycStatus.tsx';
import { ConfigureDocumentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentLegalDocuments/ConfigureDocumentDrawer/ConfigureDocumentDrawer.tsx';
import { InvestmentWorkflowKycSettingsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowKycSettingsDrawer/InvestmentWorkflowKycSettingsDrawer.tsx';
import { InvestmentWorkflowInvestorDataDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowInvestorDataDrawer/InvestmentWorkflowInvestorDataDrawer.tsx';
import { TaxAndComplianceDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TaxAndComplianceDrawer/TaxAndComplianceDrawer.tsx';
import { InvestmentDetailsLegalDocument } from '@dealstack/shared/lib/generated';
import { useNavigateToKycPage } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useNavigateToKycPage.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { useInvestmentWorkflowInvestorData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowInvestorDataDrawer/useInvestmentWorkflowInvestorData.ts';
import { useInvestmentDetailsKycStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsKycStatus.ts';
import { useInvestmentDetailsOtherData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsOtherData.ts';
import { useInvestmentDetailsTaxCompliance } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsTaxCompliance.ts';

export const InvestmentDetailsContent = () => {
	const { navigateToKycPage } = useNavigateToKycPage();
	const { mipId } = useRequiredParams('mipId');
	const { data: summary } = useInvestmentDetailsSummary();
	const { refetch: refetchKyc } = useInvestmentDetailsKycStatus();
	const [configuringDoc, setConfiguringDoc] = useState<InvestmentDetailsLegalDocument | null>(
		null
	);
	const [kycDrawerOpened, { open: openKycDrawer, close: closeKycDrawer }] = useDisclosure(false);
	const [bankDrawerOpened, setBankDrawerOpened] = useState(false);
	const [
		investorDataDrawerOpened,
		{ open: openInvestorDataDrawer, close: closeInvestorDataDrawer },
	] = useDisclosure(false);
	const [legalDocsDrawerOpened, { open: openLegalDocsDrawer, close: closeLegalDocsDrawer }] =
		useDisclosure(false);

	const investorId = summary?.investor.id;
	const investorPersonId = summary?.investor.personId ?? null;
	const pivPersonId = summary?.piv?.id ?? null;
	const isPivInvestment = pivPersonId !== null;

	const bankOwnerPersonId = pivPersonId ?? investorPersonId;
	const kycWorkflowId = summary?.kycWorkflowId ?? undefined;
	const {
		individualSettings,
		vehicleSettings,
		refetch: refetchInvestorRequirements,
	} = useInvestmentWorkflowInvestorData(kycWorkflowId);
	const {
		data: taxComplianceData,
		isLoading: taxComplianceLoading,
		refetch: refetchTaxCompliance,
	} = useInvestmentDetailsTaxCompliance();
	const [
		taxComplianceDrawerOpened,
		{ open: openTaxComplianceDrawer, close: closeTaxComplianceDrawer },
	] = useDisclosure(false);
	const showTaxCompliance =
		taxComplianceLoading || (taxComplianceData?.hasProgrammeTaxComplianceForms ?? false);
	const {
		data: otherData,
		isLoading: otherDataLoading,
		refetch: refetchOtherData,
	} = useInvestmentDetailsOtherData();

	const showOtherData = otherDataLoading || (otherData?.hasProgrammeCustomDataFields ?? false);

	const {
		data: bankAccounts = [],
		refetch: refetchBankAccounts,
		isLoading: bankAccountsLoading,
	} = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_BANKACCOUNT_GET, bankOwnerPersonId],
		queryFn: () => (bankOwnerPersonId ? getPersonBankAccounts(bankOwnerPersonId) : []),
		enabled: bankDrawerOpened && !!bankOwnerPersonId,
	});
	const { data: bankInformation, refetch: refetchBankInformation } =
		useInvestmentDetailsBankInformation();
	const selectedBankAccountId = bankInformation?.selectedBankAccountId ?? null;
	// An investment through a PIV collects the vehicle's account, so the vehicle flag governs it.
	const isBankAccountRequired = isPivInvestment
		? vehicleSettings.bankAccountDetails.checked
		: individualSettings.bankAccountDetails.checked;
	const canMutate = summary ? !summary.isReleased : false;

	return (
		<Flex direction="column" gap="1rem">
			<InvestmentAllocations />

			<InvestmentLegalDocuments onManageRequirements={openLegalDocsDrawer} />

			<ConfigureDocumentDrawer
				document={configuringDoc}
				onClose={() => setConfiguringDoc(null)}
			/>

			<Flex direction="column" gap="0.5rem">
				<SimpleGrid cols={2} spacing="0.5rem">
					<InvestmentBankInformation
						onManageRequirements={() => setBankDrawerOpened(true)}
						canMutate={canMutate}
					/>

					{showOtherData && (
						<InvestmentOtherData
							data={otherData}
							onManageRequirements={openInvestorDataDrawer}
							canMutate={canMutate}
						/>
					)}

					{showTaxCompliance && (
						<InvestmentTaxCompliance
							onOpenTaxCompliance={() => {
								if (!investorId) {
									return;
								}

								navigateToKycPage({ investorId, kycWorkflowId });
							}}
							onManageForms={openTaxComplianceDrawer}
							canMutate={canMutate}
						/>
					)}

					<InvestmentKycStatus
						onManageRequirements={openKycDrawer}
						onReviewKyc={() => {
							if (!investorId) {
								return;
							}

							navigateToKycPage({ investorId, kycWorkflowId });
						}}
						canMutate={canMutate}
					/>
				</SimpleGrid>
			</Flex>

			<InvestmentWorkflowKycSettingsDrawer
				mipId={mipId}
				kycWorkflowId={kycWorkflowId}
				opened={kycDrawerOpened}
				onClose={closeKycDrawer}
				onKycRequiredToggle={() => refetchKyc()}
			/>

			<InvestmentWorkflowInvestorDataDrawer
				kycWorkflowId={kycWorkflowId}
				opened={investorDataDrawerOpened}
				onClose={() => {
					closeInvestorDataDrawer();
					void refetchOtherData();
				}}
			/>

			<TaxAndComplianceDrawer
				mipId={mipId}
				kycWorkflowId={kycWorkflowId}
				opened={taxComplianceDrawerOpened}
				onClose={() => {
					closeTaxComplianceDrawer();
					void refetchTaxCompliance();
				}}
			/>

			<ManageLegalDocumentsDrawer
				opened={legalDocsDrawerOpened}
				onClose={closeLegalDocsDrawer}
			/>

			<ManageBankDrawer
				opened={bankDrawerOpened}
				onClose={() => setBankDrawerOpened(false)}
				onSaved={() => {
					void refetchBankInformation();
					void refetchBankAccounts();
					void refetchInvestorRequirements();
				}}
				personId={bankOwnerPersonId}
				selectedBankAccountId={selectedBankAccountId}
				bankAccounts={bankAccounts}
				isLoading={bankAccountsLoading}
				kycWorkflowId={kycWorkflowId}
				isBankAccountRequired={isBankAccountRequired}
				isPivInvestment={isPivInvestment}
			/>
		</Flex>
	);
};
